// =============================================================================
// Time Constants
// =============================================================================

/** One day in milliseconds */
export const ONE_DAY_MS = 24 * 60 * 60 * 1000;

/** Minimum pod age for over-provisioning detection (8 hours).
 * Pods younger than this are skipped to avoid false positives from insufficient data. */
export const MIN_POD_AGE_MS = 8 * 60 * 60 * 1000;

/** Grace period for transient pod statuses (5 minutes).
 * Pods in transient states (e.g., ContainerCreating, PodInitializing) for less than this duration
 * are not flagged to avoid false positives during normal startup (especially image pulls). */
export const POD_STATUS_GRACE_PERIOD_MS = 5 * 60 * 1000;

// =============================================================================
// Pod Status Constants
// =============================================================================

/**
 * Pod status reasons that indicate a critical or warning state.
 * Used by pod-analyzer for severity classification.
 */
export const POD_STATUS_ERROR_REASONS = new Set([
  "CreateContainerConfigError",
  "CreateContainerError",
  "CrashLoopBackOff",
  "ErrImagePull",
  "ImagePullBackOff",
  "InvalidImageName",
  "OOMKilled",
  "RunContainerError",
]);

/**
 * Transient pod status reasons that require a grace period before alerting.
 * These statuses are normal during pod startup and only indicate problems if they persist
 * beyond POD_STATUS_GRACE_PERIOD_MS.
 */
export const TRANSIENT_STATUS_REASONS = new Set([
  "ContainerCreating", // Normal during image pull and container setup
  "PodInitializing", // Normal during init container execution
]);

/**
 * Extended set of problematic pod status reasons.
 * Used by diagnostics for detecting problematic pods.
 */
export const PROBLEMATIC_STATUS_REASONS = new Set([
  ...POD_STATUS_ERROR_REASONS,
  "ContainerCreating", // Can indicate stuck pod
  "PodInitializing", // Can indicate stuck init container
  "Error",
  "Evicted",
]);

/**
 * Pod phases that indicate a problem (not Running or Succeeded).
 */
export const PROBLEMATIC_PHASES = new Set([
  "Pending",
  "Failed",
  "Unknown",
]);

// =============================================================================
// Namespace Exclusions
// =============================================================================

/**
 * Namespaces excluded from over-provisioning checks.
 * System namespaces where resource optimization is not recommended.
 */
export const EXCLUDED_NAMESPACES_FOR_OVERPROVISIONING =
  new Set([
    "kube-system",
    "longhorn-system",
    "argocd",
    "bl-system",
  ]);

// =============================================================================
// Default Thresholds
// =============================================================================

/**
 * Default thresholds for node-level metrics analysis.
 */
export const DEFAULT_NODE_THRESHOLDS = {
  /** Disk usage warning threshold (%) */
  diskWarning: 70,
  /** Disk usage critical threshold (%) */
  diskCritical: 85,
  /** Image count critical threshold */
  imageCountCritical: 500,
  /** CPU overcommit warning threshold (%) - requests/allocatable */
  overcommitWarning: 120,
  /** CPU overcommit critical threshold (%) */
  overcommitCritical: 150,
  /** Memory requests warning threshold (%) */
  memoryRequestsWarning: 90,
} as const;

/**
 * Default thresholds for pod-level over-provisioning detection.
 */
export const DEFAULT_OVERPROVISIONING_THRESHOLDS = {
  /** Minimum CPU limit (millicores) to check for over-provisioning.
   * Pods with lower limits are skipped (200m = 0.2 cores). */
  minCpuLimitMillicores: 200,
  /** Minimum memory limit (bytes) to check for over-provisioning (256Mi). */
  minMemoryLimitBytes: 268_435_456,
  /** CPU usage threshold (%) below which pod is considered over-provisioned */
  cpuUsagePercent: 15,
  /** Memory usage threshold (%) below which pod is considered over-provisioned */
  memoryUsagePercent: 20,
  /** Savings multiplier for hysteresis (default 2 = only alert if >50% savings).
   * current_limit > suggested * multiplier triggers alert. */
  savingsMultiplier: 2,
} as const;

/**
 * Default thresholds for pod utilization warnings.
 */
export const DEFAULT_UTILIZATION_THRESHOLDS = {
  /** CPU utilization warning threshold (%) */
  cpuWarning: 80,
  /** CPU utilization critical threshold (%) */
  cpuCritical: 95,
  /** Memory utilization warning threshold (%) */
  memoryWarning: 80,
  /** Memory utilization critical threshold (%) */
  memoryCritical: 95,
  /** Restart count threshold for warnings */
  restartCount: 5,
} as const;

// =============================================================================
// Suggestion Calculation Constants
// =============================================================================

/**
 * Constants for calculating suggested resource limits and requests.
 */
export const SUGGESTION_CONSTANTS = {
  // === Limit Constants ===
  /** Headroom multiplier for CPU limits (2.5x = 150% headroom above max usage) */
  cpuHeadroomMultiplier: 2.5,
  /** Headroom multiplier for memory limits (1.5x = 50% headroom above max usage) */
  memoryHeadroomMultiplier: 1.5,
  /** Minimum suggested CPU limit (millicores) - never suggest below 200m */
  minCpuLimitMillicores: 200,
  /** Minimum suggested memory limit (bytes) - 256Mi */
  minMemoryLimitBytes: 268_435_456,

  // === Request Constants ===
  /** Headroom multiplier for requests (1.5x = 50% headroom above avg usage) */
  requestHeadroomMultiplier: 1.5,
  /** Minimum ratio of limit to request (1.5x = limit should be at least 1.5x request) */
  limitToRequestRatio: 1.5,
  /** Minimum suggested CPU request (millicores) - never suggest below 50m */
  minCpuRequestMillicores: 50,
  /** Minimum suggested memory request (bytes) - 128Mi */
  minMemoryRequestBytes: 134_217_728,
} as const;

/**
 * Type for over-provisioning configuration (matches agent config).
 */
export type OverprovisioningConfig = {
  minCpuLimitMillicores: number;
  minMemoryLimitBytes: number;
  cpuUsagePercent: number;
  memoryUsagePercent: number;
  savingsMultiplier: number;
};

/**
 * Get over-provisioning config with defaults.
 */
export function getOverprovisioningConfig(
  overrides?: Partial<OverprovisioningConfig>
): OverprovisioningConfig {
  return {
    minCpuLimitMillicores:
      overrides?.minCpuLimitMillicores ??
      DEFAULT_OVERPROVISIONING_THRESHOLDS.minCpuLimitMillicores,
    minMemoryLimitBytes:
      overrides?.minMemoryLimitBytes ??
      DEFAULT_OVERPROVISIONING_THRESHOLDS.minMemoryLimitBytes,
    cpuUsagePercent:
      overrides?.cpuUsagePercent ??
      DEFAULT_OVERPROVISIONING_THRESHOLDS.cpuUsagePercent,
    memoryUsagePercent:
      overrides?.memoryUsagePercent ??
      DEFAULT_OVERPROVISIONING_THRESHOLDS.memoryUsagePercent,
    savingsMultiplier:
      overrides?.savingsMultiplier ??
      DEFAULT_OVERPROVISIONING_THRESHOLDS.savingsMultiplier,
  };
}
